import { EntityLabel, PBIReport } from '@esg/esg-global-types';
import { generateUuidV4 } from '../../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { apollo_client } from '../db_util';
import { GET_PBI_REPORTS_QUERY } from '../../graphql/queries/pbi_report';
import {
  CREATE_PBI_REPORT_MUTATOR,
  DELETE_PBI_REPORT_MUTATOR,
  UPDATE_PBI_REPORT_MUTATOR
} from '../../graphql/mutations/pbi_report';
import { DocumentReference } from 'firebase/firestore';
import { CompanyExtended } from '../company';
import { ReportLevelFilters, BasicFilterOperators } from 'powerbi-models';
import { models } from 'powerbi-client';
import { API_ENDPOINT } from '../../../env';
import axios, { AxiosResponse } from 'axios';
import { getAuthHeaderForUser } from '../../auth/auth.util';

export type PBIReportData = Omit<PBIReport, 'id'>;

export interface PBIReportExtended extends PBIReport {
  company: CompanyExtended;
}

export interface PowerBIFilter {
  table: string;
  column: string;
  operator: BasicFilterOperators;
  values: Array<string | number | boolean>;
}

// Singular and plural label for model entity.
export const pbi_report_label: EntityLabel = {
  one: 'PowerBI Report',
  many: 'PowerBI Reports'
};

/**
 * Query all PBIReport documents for all companies or specific companies.
 * @param {string | undefined} group_id Optional specific Group to query PBIReports for.
 * @param {string | undefined} company_id Optional specific Company to query PBIReports for.
 * @param {string | undefined} id Optional query for a specific PBIReport document.
 * @returns {Promise<Array<PBIReport | PBIReportExtended>>}
 */
export const getPBIReports = async (
  group_id?: string,
  company_id?: string,
  id?: string
): Promise<Array<PBIReport | PBIReportExtended>> => {
  try {
    const pbi_reports: Array<PBIReport | PBIReportExtended> = await apollo_client
      .query({
        query: GET_PBI_REPORTS_QUERY,
        variables: { group_id: group_id, company_id: company_id, id: id },
        fetchPolicy: 'no-cache'
      })
      .then((response) => response.data.pbi_reports);
    return pbi_reports;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: getPBIReports failed on an unknown error.',
      {
        group_id: group_id,
        company_id: company_id,
        id: id
      },
      tracking_id
    );
  }
};

/**
 * Create PBIReport with relative data.
 * @param {string} group_id Group to create PBI Report for.
 * @param {string} company_id Company to create PBI Report for.
 * @param {PBIReportData} pbi_report_data Data to create PBI Report with.
 * @param {string} user_email User creating PBI Report for audit logs.
 * @returns {Promise<DocumentReference>}
 */
export const createPBIReport = async (
  group_id: string,
  company_id: string,
  pbi_report_data: PBIReportData,
  user_email: string
): Promise<DocumentReference> => {
  try {
    const pbi_report_doc: DocumentReference = await apollo_client
      .mutate({
        mutation: CREATE_PBI_REPORT_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          pbi_report_data: pbi_report_data,
          user_email: user_email
        }
      })
      .then((response) => response.data.createPBIReport);
    return pbi_report_doc;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/pbi_report.ts failed on an unknown error while calling createPBIReport.',
      {
        group_id: group_id,
        company_id: company_id,
        pbi_report_data: pbi_report_data,
        user_email: user_email
      },
      tracking_id
    );
  }
};

/**
 * Update PBIReport with relative data.
 * @param {string} group_id ID of Group to update PBIReport for.
 * @param {string} company_id ID of Company to update PBIReport for.
 * @param {string} pbi_report_id ID of PBI Report to update.
 * @param {PBIReportData} updated_pbi_report New PBIReport data to push into document.
 * @param {PBIReportData} original_pbi_report Original PBIReport data to overwrite.
 * @param {string} user_email User updating PBI Report for audit logs.
 * @returns {Promise<void>}
 */
export const updatePBIReport = async (
  group_id: string,
  company_id: string,
  pbi_report_id: string,
  updated_pbi_report: PBIReportData,
  original_pbi_report: PBIReport | PBIReportData,
  user_email: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: UPDATE_PBI_REPORT_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          pbi_report_id: pbi_report_id,
          updated_pbi_report: updated_pbi_report,
          original_pbi_report: original_pbi_report,
          user_email: user_email
        }
      })
      .then((response) => response.data.updatePBIReport);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/pbi_report.ts failed on an unknown error while calling updatePBIReport.',
      {
        group_id: group_id,
        company_id: company_id,
        pbi_report_id: pbi_report_id,
        updated_pbi_report: updated_pbi_report,
        original_pbi_report: original_pbi_report,
        user_email
      },
      tracking_id
    );
  }
};

/**
 * Delete configured PBIReport.
 * @param {string} group_id ID of Group to delete PBI Report for.
 * @param {string} company_id ID of Company to delete PBI Report for.
 * @param {string} pbi_report_id ID of PBI Report to delete.
 * @param {string} user_email User deleting PBI Report for audit logs.
 * @returns {Promise<void>}
 */
export const deletePBIReport = async (
  group_id: string,
  company_id: string,
  pbi_report_id: string,
  user_email: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_PBI_REPORT_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          pbi_report_id: pbi_report_id,
          user_email: user_email
        }
      })
      .then((response) => response.data.deletePBIReport);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/pbi_report.ts failed on an unknown error while calling deletePBIReport.',
      {
        group_id: group_id,
        company_id: company_id,
        pbi_report_id: pbi_report_id,
        user_email: user_email
      },
      tracking_id
    );
  }
};

/**
 * Triggers a refresh of a Power BI dataset via the backend REST API.
 * @param {string} dataset_id ID of the Power BI dataset to refresh.
 * @returns {Promise<AxiosResponse>} Axios response from the refresh request.
 */
export const refreshPowerBIReport = async (dataset_id: string): Promise<AxiosResponse> => {
  try {
    const refresh_response: AxiosResponse = await axios({
      method: 'post',
      url: `${API_ENDPOINT}/refresh_pbi_report`,
      data: { dataset_id },
      headers: {
        ...(await getAuthHeaderForUser())
      },
      validateStatus: (status) => status === 200 || status === 409 || status === 429
    });

    return refresh_response;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/app/pbi_report.ts failed on an unknown error while calling refreshPowerBIReport.',
      {
        dataset_id: dataset_id
      },
      tracking_id
    );
  }
};

/**
 * Dynamically generate formatted filters to insert into powerbi view configurations.
 * @param {string} table PowerBI model source table to filter on.
 * @param {string} column PowerBI model source table column to filter on.
 * @param {BasicFilterOperators} operator Filter method to apply to column.
 * @param {Array<string | number | boolean>} values Values to filter for within column using operator.
 * @returns {Array<ReportLevelFilters>}
 */
export const generatePowerBIReportFilters = (
  filters: Array<PowerBIFilter>
): Array<ReportLevelFilters> => {
  return filters.map((filter: PowerBIFilter) => ({
    $schema: 'http://powerbi.com/product/schema#basic',
    target: {
      table: filter.table,
      column: filter.column
    },
    operator: filter.operator,
    values: filter.values,
    filterType: models.FilterType.Basic,
    requireSingleSelection: true
  }));
};

/**
 * Common filter for user email security on PowerBI model data.
 */
export const POWERBI_REPORT_FILTERS_USER_EMAIL = (
  user_email: string,
  table = 's_configuration_users',
  column = 'user_email'
): Array<ReportLevelFilters> => {
  const user_email_filters: Array<PowerBIFilter> = [
    {
      table: table,
      column: column,
      operator: 'In',
      values: [user_email]
    }
  ];
  return generatePowerBIReportFilters(user_email_filters);
};

/**
 * Gets the report_id value from an embed_url.
 * @param {string} embed_url The PowerBI-generated URL used to embed the report.
 * @returns {string}
 */
export const getReportId = (embed_url: string): string => {
  const url_params = new URLSearchParams(embed_url.split('?')[1]);
  return url_params.get('reportId') || '';
};

/**
 * Checks if the input power bi report is valid.
 * @param {PBIReport | null} report PowerBI report to validate.
 * @returns {boolean}
 */
export const validateReport = (report: PBIReport | null): report is PBIReport => {
  if (!report) return false;
  if (!getReportId(report.embed_url)) return false;
  if (typeof report.view !== 'string') return false;
  return true;
};
